import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from 'react-share';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 30,
            top: 30,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function ProjectSocialShare({project} :any) {
  const [open, setOpen] = React.useState(false);
  const [checked, setChecked] = React.useState(project.public || false);

  const handleClickOpen = (e) => {
    e.preventDefault();
    updateProject(project.id, true);
    setChecked(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  function copyMessage(e, content) {
    navigator.clipboard.writeText(content);
  }

  function updateProject(project_id, makePublic) {
    const url = "/projects/" + project_id;
    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({'public': makePublic})
    }).then(response => response.json())
      .then(data => {
        console.log('Success:', data);
      })
  }

  function handleChecked(e) {
    e.preventDefault();
    const newChecked = !checked;
    setChecked(newChecked);
    updateProject(project.id, newChecked);
  }

  return (
    <>
      <Tooltip title="Share Project">
        <button onClick={(e) => handleClickOpen(e)} className="btn btn-outline-info" >
          <ShareIcon fontSize="small" /> Share
        </button>
      </Tooltip>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          Share Project
        </BootstrapDialogTitle>

        <div className="row">
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <DialogContent dividers>
                  <div className="row">
                    <div className="col-md-2">
                      <TwitterShareButton
                        url={project.public_url}
                        title={project.title}
                      >
                        <XIcon size={32} round />
                      </TwitterShareButton>
                    </div>
                    <div className="col-md-2">
                      <RedditShareButton
                        url={project.public_url}
                        title={project.title}
                        windowWidth={660}
                        windowHeight={460}
                      >
                        <RedditIcon size={32} round />
                      </RedditShareButton>
                    </div>

                    <div className="col-md-2">
                      <FacebookShareButton url={project.public_url}>
                        <FacebookIcon size={32} round />
                      </FacebookShareButton>
                    </div>
                    <div className="col-md-2">
                      <WhatsappShareButton
                        url={project.public_url}
                        title={project.title}
                        separator=":: "
                      >
                        <WhatsappIcon size={32} round />
                      </WhatsappShareButton>
                    </div>

                    <div className='col-md-2'>
                      <LinkedinShareButton url={project.public_url}>
                        <LinkedinIcon size={32} round />
                      </LinkedinShareButton>
                    </div>

                    <div className='col-md-2'>
                      <EmailShareButton
                        url={project.public_url}
                        subject={project.title}
                        body="Check out this project!"
                      >
                        <EmailIcon size={32} round />
                      </EmailShareButton>
                    </div>
                  </div>
                </DialogContent>
                <div className="row">
                  <DialogContent dividers>
                    <div className="form-group">
                      <div className="input-group">
                        <input type="text" className="form-control" value={project.public_url} aria-label="Project URL" aria-describedby="basic-addon2" disabled />
                        <div className="input-group-append">
                          <Tooltip title="Copy">
                            <IconButton onClick={(e) => copyMessage(e, project.public_url)} size="small" aria-label="Copy">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </div>
                <div className="row">
                  <DialogContent dividers>
                    <p>
                      <h3>Permissions</h3>
                      <Switch
                        onChange={(e) => handleChecked(e)}
                        size="small"
                        color="warning"
                        checked={checked}
                      />
                      Anyone with this link can view this project.
                    </p>
                  </DialogContent>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
}
